import { Modal, Typography } from '@yimoka/antd';
import { useEffect, useRef, useState } from 'react';

import { getEnv } from '@/env';
import { useTranslation } from '@/locales/i18n';
import type { ModuleVersionMap } from '@/mf/types';
import { fetchModuleVersionMap, getChangedModuleNames } from '@/mf/version-map';

const updateInterval = 5 * 60 * 1000;
const visibilityCheckInterval = 60 * 1000;

interface PendingModuleUpdate {
  signature: string;
  titles: string[];
}

/** 轮询可变版本表；已加载 remote 不做热替换，避免共享单例出现新旧代码混用。 */
export function ModuleUpdatePrompt({ versionMap }: { versionMap: ModuleVersionMap }) {
  const { t } = useTranslation();
  const [pendingUpdate, setPendingUpdate] = useState<PendingModuleUpdate>();
  const dismissedSignatureRef = useRef('');

  useEffect(() => {
    const env = getEnv();
    if (!env.moduleVersionsUrl) return;

    let disposed = false;
    let checking = false;
    let lastCheckTime = Date.now();

    const checkUpdate = async () => {
      if (checking) return;
      checking = true;
      lastCheckTime = Date.now();
      try {
        const next = await fetchModuleVersionMap(env.moduleVersionsUrl, {
          fallbackOnError: false,
          tenantID: env.tenantID,
        });
        if (disposed) return;
        const changedNames = getChangedModuleNames(versionMap, next);
        if (!changedNames.length) return;

        const signature = changedNames.map(name => `${name}:${next.modules[name]?.current ?? 'removed'}`).join('|');
        if (signature === dismissedSignatureRef.current) return;
        setPendingUpdate({
          signature,
          titles: changedNames.map(name => next.modules[name]?.title ?? name),
        });
      } catch {
        // 后台检查失败时保留当前已验证版本，不用内置版本表制造错误的回滚提示。
      } finally {
        checking = false;
      }
    };

    const timer = window.setInterval(() => void checkUpdate(), updateInterval);
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && Date.now() - lastCheckTime >= visibilityCheckInterval) {
        void checkUpdate();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      disposed = true;
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [versionMap]);

  return (
    <Modal
      destroyOnHidden
      cancelText={t('Update:later')}
      mask={{ closable: false }}
      okText={t('Update:refreshNow')}
      open={Boolean(pendingUpdate)}
      title={t('Update:module.title')}
      trigger={false}
      width={420}
      zIndex={9998}
      onCancel={() => {
        dismissedSignatureRef.current = pendingUpdate?.signature ?? '';
        setPendingUpdate(undefined);
      }}
      onOk={() => window.location.reload()}
    >
      <Typography.Paragraph style={{ margin: 0 }}>
        {t('Update:module.description', { modules: pendingUpdate?.titles.join('、') ?? '' })}
      </Typography.Paragraph>
    </Modal>
  );
}
